
import JSZip from 'jszip';

/**
 * Parses a raw SVG string, removes unnecessary metadata/cruft,
 * and returns a clean SVG string.
 */
export const cleanSVG = (svgString: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svg = doc.querySelector("svg");

  if (!svg) return svgString;

  // Remove common unnecessary attributes
  const attrsToRemove = ['id', 'class', 'data-name', 'xmlns:xlink', 'style', 'xml:space'];
  attrsToRemove.forEach(attr => svg.removeAttribute(attr));

  // Ensure viewBox exists
  if (!svg.getAttribute('viewBox') && svg.getAttribute('width') && svg.getAttribute('height')) {
    svg.setAttribute('viewBox', `0 0 ${svg.getAttribute('width')} ${svg.getAttribute('height')}`);
  }

  // Remove width/height to allow CSS scaling, purely rely on viewBox
  // svg.removeAttribute('width');
  // svg.removeAttribute('height');
  svg.style.width = '100%';
  svg.style.height = '100%';

  // Remove scripts or dangerous tags
  const scripts = svg.querySelectorAll('script');
  scripts.forEach(s => s.remove());

  return new XMLSerializer().serializeToString(svg);
};

/**
 * Takes an SVG string and forces all fill/stroke colors to a specific hex.
 */
export const colorizeSVG = (svgString: string, hexColor: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svg = doc.querySelector("svg");

  if (!svg) return svgString;

  const elements = svg.querySelectorAll('*');
  elements.forEach(el => {
    // If element has a fill that isn't none, change it
    const fill = el.getAttribute('fill');
    if (fill && fill !== 'none') {
      el.setAttribute('fill', hexColor);
    }
    
    // Handle style attributes
    if (el.getAttribute('style')) {
      el.removeAttribute('style'); // Nuke styles for clean override
      el.setAttribute('fill', hexColor);
    }

    // Apply to path if no attributes exist (often implies black)
    if (el.tagName === 'path' && !el.getAttribute('fill')) {
      el.setAttribute('fill', hexColor);
    }
    
    // Handle strokes
    const stroke = el.getAttribute('stroke');
    if (stroke && stroke !== 'none') {
      el.setAttribute('stroke', hexColor);
    }
  });

  return new XMLSerializer().serializeToString(svg);
};

/**
 * Decodes a base64 SVG, replaces all occurrences of oldColor with newColor,
 * and re-encodes it. Case insensitive.
 */
export const replaceColorInBase64Svg = (base64Str: string, oldColor: string, newColor: string): string => {
  if (!base64Str.includes('image/svg+xml')) return base64Str;

  try {
    const header = "data:image/svg+xml;base64,";
    const rawBase64 = base64Str.replace(header, "");
    const decoded = atob(rawBase64);

    // Escape regex characters just in case, though hex codes are safe
    const escapedOld = oldColor.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    
    // Global, Case-insensitive replacement
    const regex = new RegExp(escapedOld, 'gi');
    const updatedSvg = decoded.replace(regex, newColor);

    return `${header}${btoa(updatedSvg)}`;
  } catch (e) {
    console.error("Failed to recolor SVG", e);
    return base64Str;
  }
};

export const convertSvgToBase64 = (svgString: string): string => {
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

/**
 * Wraps a base64 image in an SVG with a color overlay rectangle.
 * Uses preserveAspectRatio="xMidYMid slice" to mimic object-cover behavior inside the SVG.
 */
export const createImageWithOverlay = (base64Image: string, overlayHex: string, opacity: number = 0.5): string => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none">
      <defs>
        <clipPath id="clip">
           <rect width="100%" height="100%" />
        </clipPath>
      </defs>
      <image href="${base64Image}" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" clip-path="url(#clip)" />
      <rect width="100%" height="100%" fill="${overlayHex}" fill-opacity="${opacity}" />
    </svg>
  `;
  return convertSvgToBase64(svg);
};

/**
 * Compresses a base64 image string by resizing it to a max dimension (e.g. 1024px)
 * and adjusting quality.
 * Vercel Serverless Function Limit is 4.5MB. We need to be aggressive.
 */
export const compressBase64Image = (base64Str: string, maxWidth = 1024, quality = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    // If it's an SVG, don't compress
    if (base64Str.includes('image/svg+xml')) {
      resolve(base64Str);
      return;
    }

    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(base64Str);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      // Export as JPEG for better compression, even if source was PNG
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = (err) => {
      console.warn("Image compression failed, using original", err);
      resolve(base64Str);
    };
  });
};

/**
 * Converts a Hex color code to a CMYK string (C:0 M:0 Y:0 K:100).
 */
export const hexToCmyk = (hex: string): string => {
  let c = 0;
  let m = 0;
  let y = 0;
  let k = 0;

  hex = (hex.charAt(0) === "#") ? hex.substring(1, 7) : hex;

  if (hex.length !== 6) {
    return "C:0 M:0 Y:0 K:100";
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Black
  if (r === 0 && g === 0 && b === 0) {
    return "C:0 M:0 Y:0 K:100";
  }

  const calculatedC = 1 - (r / 255);
  const calculatedM = 1 - (g / 255);
  const calculatedY = 1 - (b / 255);

  const minCMY = Math.min(calculatedC, Math.min(calculatedM, calculatedY));

  c = (calculatedC - minCMY) / (1 - minCMY);
  m = (calculatedM - minCMY) / (1 - minCMY);
  y = (calculatedY - minCMY) / (1 - minCMY);
  k = minCMY;

  return `C:${Math.round(c * 100)} M:${Math.round(m * 100)} Y:${Math.round(y * 100)} K:${Math.round(k * 100)}`;
};

/**
 * Downloads a Base64 SVG as a PNG
 */
export const downloadAsPng = (svgDataUrl: string, fileName: string, backgroundColor?: string) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const img = new Image();

  img.onload = () => {
    // Set high res for quality
    canvas.width = 1080;
    canvas.height = 1080;
    
    if (ctx) {
      // Draw background if exists
      if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // Draw image centered
      ctx.drawImage(img, 0, 0, 1080, 1080);
      
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${fileName}.png`;
      link.href = pngUrl;
      link.click();
    }
  };

  img.src = svgDataUrl;
};

export const downloadAsSvg = (svgDataUrl: string, fileName: string) => {
  const link = document.createElement('a');
  link.download = `${fileName}.svg`;
  link.href = svgDataUrl;
  link.click();
};

export const downloadAsZip = async (assets: { url: string, name: string }[]) => {
  const zip = new JSZip();

  // Helper to get blob from data URL
  const dataURLtoBlob = async (dataurl: string) => {
      const res = await fetch(dataurl);
      return await res.blob();
  };

  for (const asset of assets) {
      // Basic detection
      const isSvg = asset.url.includes('image/svg+xml');
      const extension = isSvg ? 'svg' : 'jpg'; // Default to jpg for non-svgs in zip
      const blob = await dataURLtoBlob(asset.url);
      zip.file(`${asset.name}.${extension}`, blob);
  }

  const content = await zip.generateAsync({ type: "blob" });
  
  // Trigger download
  const link = document.createElement("a");
  link.href = URL.createObjectURL(content);
  link.download = "brand-assets.zip";
  link.click();
};
